import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  generateAndPrepareCampaign,
  createAndSendCampaign,
  importCSVContacts,
  parseCSVFile,
  getCampaignStats,
  generateSocialPosts
} from '../../services/aiCampaignService';

const SITE_URL = 'https://the-apn.web.app';

const CAMPAIGN_TYPES = [
  { id: 'weekly_digest', name: 'Weekly Digest', icon: '📰', desc: 'Roundup of events, jobs, projects & news' },
  { id: 'event_promo', name: 'Event Promotion', icon: '🎉', desc: 'Promote an upcoming event' },
  { id: 'new_jobs_alert', name: 'New Jobs Alert', icon: '💼', desc: 'Alert about new job postings' },
  { id: 're_engagement', name: 'Re-engagement', icon: '🔄', desc: 'Win back inactive contacts' },
  { id: 'welcome_drip', name: 'Welcome Series', icon: '👋', desc: 'Onboard new members' },
  { id: 'custom', name: 'Custom Campaign', icon: '✍️', desc: 'Write your own topic for Claude' },
];

export default function CampaignDashboard() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const [activeTab, setActiveTab] = useState('create');
  const [selectedType, setSelectedType] = useState(null);
  const [customPrompt, setCustomPrompt] = useState('');
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [claudeResponse, setClaudeResponse] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [subject, setSubject] = useState('');
  const [siteData, setSiteData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [mode, setMode] = useState('claude-assist');
  const [campaignHistory, setCampaignHistory] = useState([]);
  const [csvContacts, setCsvContacts] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [socialTopic, setSocialTopic] = useState('');
  const [socialPosts, setSocialPosts] = useState(null);
  const [socialLoading, setSocialLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const promptRef = useRef(null);

  useEffect(() => {
    if (isAdmin) { fetchSiteData(); fetchCampaignHistory(); }
  }, [isAdmin]);

  async function fetchSiteData() {
    try {
      const [ev, jo, pr, ar, us] = await Promise.all([
        getDocs(query(collection(db, 'events'), orderBy('date', 'desc'), limit(5))).catch(() => ({ docs: [] })),
        getDocs(query(collection(db, 'jobs'), orderBy('postedDate', 'desc'), limit(5))).catch(() => ({ docs: [] })),
        getDocs(query(collection(db, 'projects'), orderBy('postedDate', 'desc'), limit(5))).catch(() => ({ docs: [] })),
        getDocs(query(collection(db, 'articles'), orderBy('publishedAt', 'desc'), limit(3))).catch(() => ({ docs: [] })),
        getDocs(collection(db, 'users')).catch(() => ({ docs: [], size: 0 })),
      ]);
      setSiteData({
        events: ev.docs.map(d => ({ id: d.id, ...d.data() })),
        jobs: jo.docs.map(d => ({ id: d.id, ...d.data() })),
        projects: pr.docs.map(d => ({ id: d.id, ...d.data() })),
        articles: ar.docs.map(d => ({ id: d.id, ...d.data() })),
        stats: { totalMembers: us.size || us.docs?.length || 0 },
      });
    } catch (err) { console.error('Error fetching site data:', err); }
  }

  async function fetchCampaignHistory() {
    try {
      const r = await getCampaignStats();
      if (r.success) setCampaignHistory(r.campaigns || []);
    } catch { /* ok */ }
  }

  function buildPrompt(type) {
    if (!siteData) return 'Loading site data...';
    const evList = siteData.events.map(e => `- ${e.title || 'Untitled'} | ${e.date || 'TBD'} | ${e.location || 'TBD'} | ${SITE_URL}/events/${e.id}`).join('\n') || 'None';
    const joList = siteData.jobs.map(j => `- ${j.title || 'Untitled'} at ${j.company || 'Company'} | ${j.location || 'Remote'} | ${SITE_URL}/jobs/${j.id}`).join('\n') || 'None';
    const prList = siteData.projects.map(p => `- ${p.title || 'Untitled'} | Budget: ${p.budgetRange || 'TBD'} | ${SITE_URL}/projects/${p.id}`).join('\n') || 'None';
    const arList = siteData.articles.map(a => `- ${a.title || 'Untitled'} | ${SITE_URL}/news/${a.id}`).join('\n') || 'None';
    const tasks = {
      weekly_digest: 'Generate a weekly digest newsletter for APN members. Include sections for upcoming events, new jobs, active projects, and recent news. Each item should link to its page.',
      event_promo: 'Generate an event promotion email for the next upcoming APN event. Create urgency, highlight key details, include a prominent "Register Now" button.',
      new_jobs_alert: 'Generate a job alert email highlighting new positions on the APN job board.',
      re_engagement: 'Generate a re-engagement email for contacts who haven\'t been active. Show what they\'re missing.',
      welcome_drip: 'Generate a welcome email for new APN members. Walk them through Events, Job Board, Project Board, Groups, and Directory.',
      custom: `Generate an email campaign about: ${customPrompt || '[Enter topic above]'}`,
    };
    return `You are the content writer for Accelerated Professionals Network (APN), a premier professional network for finance & business professionals.

BRAND: Professional but approachable. Color #3d67ad (blue) + white. Website: ${SITE_URL}
FORMAT: Inline CSS, max-width 600px, Arial font. CTA buttons: bg #3d67ad, white text, rounded, padding 12px 24px.
Include LinkedIn (https://www.linkedin.com/company/accelerated-professionals-network) and Instagram (@the_apn_) links.

LIVE DATA:
Events (${siteData.events.length}):
${evList}

Jobs (${siteData.jobs.length}):
${joList}

Projects (${siteData.projects.length}):
${prList}

Articles (${siteData.articles.length}):
${arList}

Members: ${siteData.stats.totalMembers}

TASK: ${tasks[type.id]}

Return JSON: {"subject": "...", "html": "<div>complete email HTML</div>", "previewText": "..."}`;
  }

  function handleSelectType(type) {
    setSelectedType(type);
    setClaudeResponse(''); setPreviewHtml(''); setSubject(''); setSendResult(null); setCopied(false);
    if (mode === 'claude-assist') setGeneratedPrompt(buildPrompt(type));
  }

  async function handleCopy() {
    try { await navigator.clipboard.writeText(generatedPrompt); } catch { promptRef.current?.select(); document.execCommand('copy'); }
    setCopied(true); setTimeout(() => setCopied(false), 3000);
  }

  function handleParseResponse() {
    try {
      const m = claudeResponse.match(/\{[\s\S]*\}/);
      if (m) { const p = JSON.parse(m[0]); setSubject(p.subject || ''); setPreviewHtml(p.html || ''); }
      else { setPreviewHtml(claudeResponse); setSubject(`APN ${selectedType?.name || 'Update'}`); }
    } catch { setPreviewHtml(claudeResponse); setSubject(`APN ${selectedType?.name || 'Update'}`); }
  }

  async function handleAutoGenerate() {
    if (!selectedType) return;
    setLoading(true);
    try {
      const r = await generateAndPrepareCampaign(selectedType.id, { customPrompt });
      setSubject(r.content.subject || ''); setPreviewHtml(r.content.html || '');
    } catch (err) { alert(`API error: ${err.message}\nTry "Claude Assist" mode instead.`); }
    finally { setLoading(false); }
  }

  async function handleSend() {
    if (!previewHtml || !subject) return alert('Generate content first');
    if (!window.confirm('Create this campaign as a draft in Constant Contact?')) return;
    setSending(true);
    try {
      await createAndSendCampaign({ name: `APN - ${selectedType?.name || 'Campaign'} - ${new Date().toLocaleDateString()}`, subject, htmlContent: previewHtml, listIds: [], sendNow: false });
      setSendResult({ ok: true, msg: '✅ Campaign created as draft in Constant Contact! Review & send from your CC dashboard.' });
    } catch (err) { setSendResult({ ok: false, msg: `❌ ${err.message}` }); }
    finally { setSending(false); }
  }

  function handleCSVFile(e) {
    const f = e.target.files[0]; if (!f) return;
    setImportResult(null);
    const reader = new FileReader();
    reader.onload = (ev) => setCsvContacts(parseCSVFile(ev.target.result));
    reader.readAsText(f);
  }

  async function handleImportCSV() {
    if (!csvContacts.length) return alert('No valid contacts in CSV');
    if (!window.confirm(`Import ${csvContacts.length} contacts to Constant Contact?`)) return;
    setImporting(true);
    try {
      const r = await importCSVContacts(csvContacts);
      setImportResult({ ok: true, msg: `✅ Imported ${r.imported || csvContacts.length} contacts!` });
    } catch (err) { setImportResult({ ok: false, msg: `❌ ${err.message}` }); }
    finally { setImporting(false); }
  }

  if (!isAdmin) return <div className="max-w-4xl mx-auto p-6"><div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">Admin access required.</div></div>;

  const tabs = [
    { id: 'create', name: '✍️ Create Campaign' },
    { id: 'import', name: '📁 Import Contacts' },
    { id: 'history', name: '📊 History' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">📧 Campaign Dashboard</h1>
        <p className="mt-2 text-gray-600">Create AI-powered email campaigns • Powered by Claude + Constant Contact</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="-mb-px flex space-x-8">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === t.id ? 'border-[#3d67ad] text-[#3d67ad]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {t.name}
            </button>
          ))}
        </nav>
      </div>

      {/* ===== CREATE TAB ===== */}
      {activeTab === 'create' && (
        <div className="space-y-8">
          {/* Mode Toggle */}
          <div className="flex items-center gap-4 bg-gray-50 p-4 rounded-lg">
            <span className="text-sm font-medium text-gray-700">Mode:</span>
            <button onClick={() => setMode('claude-assist')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${mode === 'claude-assist' ? 'bg-[#3d67ad] text-white' : 'bg-white text-gray-700 border'}`}>
              🧠 Claude Assist (Your $200 Subscription)
            </button>
            <button onClick={() => setMode('auto-api')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${mode === 'auto-api' ? 'bg-[#3d67ad] text-white' : 'bg-white text-gray-700 border'}`}>
              ⚡ Auto-Generate (API)
            </button>
          </div>

          {/* Campaign Type Selection */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">1. Choose Campaign Type</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {CAMPAIGN_TYPES.map(type => (
                <button key={type.id} onClick={() => handleSelectType(type)}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${selectedType?.id === type.id ? 'border-[#3d67ad] bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <div className="text-2xl mb-2">{type.icon}</div>
                  <div className="font-medium text-gray-900">{type.name}</div>
                  <div className="text-sm text-gray-500 mt-1">{type.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Custom prompt input */}
          {selectedType?.id === 'custom' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Custom Topic / Prompt</label>
              <textarea value={customPrompt} onChange={e => { setCustomPrompt(e.target.value); if (mode === 'claude-assist') setGeneratedPrompt(buildPrompt(selectedType)); }}
                rows={3} className="w-full border rounded-lg p-3 text-sm" placeholder="e.g., Announce our Spring Networking Mixer on April 15th at The Standard Hotel..." />
            </div>
          )}

          {/* CLAUDE ASSIST MODE */}
          {selectedType && mode === 'claude-assist' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900">2. Copy Prompt → Paste in Claude → Paste Result Back</h2>

              {/* Step A: Generated Prompt */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">📋 Step A: Your AI Prompt (auto-generated with live site data)</span>
                  <div className="flex gap-2">
                    <button onClick={handleCopy}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${copied ? 'bg-green-500 text-white' : 'bg-[#3d67ad] text-white hover:bg-blue-700'}`}>
                      {copied ? '✅ Copied!' : '📋 Copy Prompt'}
                    </button>
                    <button onClick={() => window.open('https://claude.ai/new', '_blank')}
                      className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600">
                      🔗 Open Claude
                    </button>
                  </div>
                </div>
                <textarea ref={promptRef} readOnly value={generatedPrompt} rows={6}
                  className="w-full border rounded-lg p-3 text-xs font-mono bg-white" />
              </div>

              {/* Step B: Paste Claude's Response */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">📥 Step B: Paste Claude's Response Here</span>
                  <button onClick={handleParseResponse} disabled={!claudeResponse}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50">
                    🔍 Parse & Preview
                  </button>
                </div>
                <textarea value={claudeResponse} onChange={e => setClaudeResponse(e.target.value)} rows={6}
                  className="w-full border rounded-lg p-3 text-xs font-mono" placeholder="Paste Claude's entire JSON response here..." />
              </div>
            </div>
          )}

          {/* AUTO-API MODE */}
          {selectedType && mode === 'auto-api' && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">2. Auto-Generate with Claude API</h2>
              <button onClick={handleAutoGenerate} disabled={loading}
                className="px-6 py-3 bg-[#3d67ad] text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
                {loading ? '⏳ Generating with Claude...' : '⚡ Generate Campaign Content'}
              </button>
              <p className="text-xs text-gray-500 mt-2">Uses your Anthropic API key (set in Firebase). Costs ~$0.01-0.05 per generation.</p>
            </div>
          )}

          {/* PREVIEW */}
          {previewHtml && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">3. Preview & Edit</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line</label>
                <input type="text" value={subject} onChange={e => setSubject(e.target.value)}
                  className="w-full border rounded-lg p-3" />
              </div>
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-100 px-4 py-2 text-sm font-medium text-gray-600">Email Preview</div>
                <div className="p-4 bg-white max-h-[500px] overflow-y-auto" dangerouslySetInnerHTML={{ __html: previewHtml }} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Edit HTML (optional)</label>
                <textarea value={previewHtml} onChange={e => setPreviewHtml(e.target.value)} rows={6}
                  className="w-full border rounded-lg p-3 text-xs font-mono" />
              </div>

              {/* Send */}
              <div className="flex items-center gap-4">
                <button onClick={handleSend} disabled={sending}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50">
                  {sending ? '⏳ Creating Campaign...' : '🚀 Create Draft in Constant Contact'}
                </button>
                <span className="text-sm text-gray-500">Creates as draft — you review & send from CC</span>
              </div>
              {sendResult && (
                <div className={`p-4 rounded-lg ${sendResult.ok ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                  {sendResult.msg}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ===== IMPORT TAB ===== */}
      {activeTab === 'import' && (
        <div className="space-y-6">
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">📁 Import CSV Contacts to Constant Contact</h2>
            <p className="text-gray-600 mb-6">Upload your CSV file with contacts. The system will parse email, first name, last name, company, title, and phone columns automatically.</p>
            <div className="space-y-4">
              <input type="file" accept=".csv" onChange={handleCSVFile}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#3d67ad] file:text-white hover:file:bg-blue-700" />
              {csvContacts.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Preview ({csvContacts.length} contacts found):</p>
                  <div className="max-h-64 overflow-y-auto border rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr><th className="px-4 py-2 text-left">Email</th><th className="px-4 py-2 text-left">First</th><th className="px-4 py-2 text-left">Last</th><th className="px-4 py-2 text-left">Company</th></tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {csvContacts.slice(0, 20).map((c, i) => (
                          <tr key={i}><td className="px-4 py-2">{c.email}</td><td className="px-4 py-2">{c.firstName}</td><td className="px-4 py-2">{c.lastName}</td><td className="px-4 py-2">{c.company || '-'}</td></tr>
                        ))}
                      </tbody>
                    </table>
                    {csvContacts.length > 20 && <p className="p-3 text-center text-gray-500 text-sm">...and {csvContacts.length - 20} more</p>}
                  </div>
                  <button onClick={handleImportCSV} disabled={importing}
                    className="mt-4 px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50">
                    {importing ? `⏳ Importing ${csvContacts.length} contacts...` : `🚀 Import ${csvContacts.length} Contacts to CC`}
                  </button>
                </div>
              )}
              {importResult && (
                <div className={`p-4 rounded-lg ${importResult.ok ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                  {importResult.msg}
                </div>
              )}
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-medium text-blue-900 mb-2">CSV Format Tips</h3>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>Must have an <strong>email</strong> column (required)</li>
              <li>Supports: first name, last name, company, title/position, phone</li>
              <li>Column headers are auto-detected (case-insensitive)</li>
              <li>Contacts without valid emails are skipped</li>
              <li>Duplicates are handled by Constant Contact (upserts)</li>
            </ul>
          </div>
        </div>
      )}

      {/* ===== HISTORY TAB ===== */}
      {activeTab === 'history' && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">📊 Campaign History</h2>
          {campaignHistory.length === 0 ? (
            <p className="text-gray-500">No campaigns sent yet. Create your first campaign above!</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr><th className="px-4 py-3 text-left font-medium text-gray-500">Name</th><th className="px-4 py-3 text-left font-medium text-gray-500">Date</th><th className="px-4 py-3 text-left font-medium text-gray-500">Status</th><th className="px-4 py-3 text-left font-medium text-gray-500">Opens</th></tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {campaignHistory.map((c, i) => (
                    <tr key={i}>
                      <td className="px-4 py-3">{c.name || 'Campaign'}</td>
                      <td className="px-4 py-3">{c.sentAt ? new Date(c.sentAt).toLocaleDateString() : '-'}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs ${c.status === 'sent' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{c.status || 'draft'}</span></td>
                      <td className="px-4 py-3">{c.opens || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

